import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import IncludeSideBar from "../../../Components/Sidebar/IncludeSideBar";
import { getUserSessions } from "../../../apis/dashboard";
import "../Overview/Analytics.css";

const SessionTracking = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const fetchSessions = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
      };
      
      if (userId) params.userId = userId;
      if (startDate) params.startDate = Math.floor(startDate.getTime() / 1000);
      if (endDate) params.endDate = Math.floor(endDate.getTime() / 1000);

      const response = await getUserSessions(
        params.userId,
        params.startDate,
        params.endDate,
        params.page,
        params.limit
      );
      
      setSessions(response?.sessions || []);
      setTotalPages(response?.totalPages || 1);
      setError(null);
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setError("Failed to fetch sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(1);
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchSessions(1);
  };

  const handleClearFilters = () => {
    setUserId("");
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
    fetchSessions(1);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    
    let date;
    if (typeof timestamp === 'number') {
      if (timestamp > 1000000000000) {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === 'string') {
      const num = parseInt(timestamp);
      if (!isNaN(num)) {
        date = num > 1000000000000 ? new Date(num) : new Date(num * 1000);
      } else {
        date = new Date(timestamp);
      }
    } else {
      return "N/A";
    }
    
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatWatchTime = (seconds) => {
    if (!seconds && seconds !== 0) return "0s";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  return (
    <IncludeSideBar>
      <div className="session-page">
        <h2>Session Tracking</h2>

        <div className="filter-container">
          <input
            type="text"
            placeholder="User ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            style={{ width: "150px" }}
          />
          
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText="Start Date"
            maxDate={new Date()}
            dateFormat="dd/MM/yyyy"
          />
          
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            placeholderText="End Date"
            minDate={startDate}
            maxDate={new Date()}
            dateFormat="dd/MM/yyyy"
          />

          <button onClick={handleSearch} className="primary">
            Search
          </button>
          
          <button onClick={handleClearFilters} className="secondary">
            Clear
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Loading sessions...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
            <button onClick={() => fetchSessions(currentPage)}>Retry</button>
          </div>
        ) : (
          <>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Login Time</th>
                    <th>Logout Time</th>
                    <th>Duration</th>
                    <th>Videos Watched</th>
                    <th>Total Watch Time</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.length > 0 ? (
                    sessions.map((session, index) => (
                      <tr key={index}>
                        <td>{session.userId || "N/A"}</td>
                        <td>{formatTimestamp(session.loginTime)}</td>
                        <td>{formatTimestamp(session.logoutTime)}</td>
                        <td>{formatDuration(session.sessionDuration)}</td>
                        <td>{session.videosWatched || 0}</td>
                        <td>{formatWatchTime(session.totalWatchTime)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center", padding: "30px" }}>
                        No sessions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => { setCurrentPage(1); fetchSessions(1); }}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  onClick={() => { const p = currentPage - 1; setCurrentPage(p); fetchSessions(p); }}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                <span>Page {currentPage} of {totalPages}</span>
                
                <button
                  onClick={() => { const p = currentPage + 1; setCurrentPage(p); fetchSessions(p); }}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button
                  onClick={() => { setCurrentPage(totalPages); fetchSessions(totalPages); }}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </IncludeSideBar>
  );
};

export default SessionTracking;
